import {
  Box,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Text
} from '@mantine/core'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  useInvalidateDeploymentList,
  useRemoveAppDeployment,
  useRemoveCameraDeployment
} from '@/queries/deploymentQueries'
import { showToast } from '@/theme/notifications'

type DeploymentDeleteConfirmProps = {
  opened: boolean
  applicationId: string
  applicationName: string
  deploymentId: string
  showOcrWarning?: boolean
  onClose: () => void
}

export const DeploymentDeleteConfirm = ({
  opened,
  applicationId,
  applicationName,
  deploymentId,
  showOcrWarning,
  onClose
}: DeploymentDeleteConfirmProps) => {
  const intl = useIntl()

  const [removeAllDeployments, setRemoveAllDeployments] = useState(false)

  const {
    mutateAsync: removeAppDeployment,
    isPending: isRemovingAppDeployment
  } = useRemoveAppDeployment()

  const {
    mutateAsync: removeCameraDeployment,
    isPending: isRemovingCameraDeployment
  } = useRemoveCameraDeployment()

  const { invalidateDeploymentList } = useInvalidateDeploymentList()

  const handleDeleteDeployment = async () => {
    try {
      if (removeAllDeployments) {
        await removeAppDeployment(applicationId)
      } else {
        await removeCameraDeployment(deploymentId)
      }

      void invalidateDeploymentList()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'deployments.delete.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="deployments.delete" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay
          visible={isRemovingAppDeployment || isRemovingCameraDeployment}
        />

        <Stack gap="md">
          <Text size="sm">
            <FormattedMessage id="deployments.delete.confirmation" />
          </Text>

          <Paper p="sm" bg="gray.1">
            <Checkbox
              label={
                <FormattedMessage
                  id="deployments.delete.all"
                  values={{
                    application: <strong>{applicationName}</strong>
                  }}
                />
              }
              checked={removeAllDeployments}
              onChange={(event) =>
                setRemoveAllDeployments(event.currentTarget.checked)
              }
            />
          </Paper>

          {showOcrWarning && (
            <PlatformAlert variant="warning" compact>
              <FormattedMessage id="deployments.delete.warning" />
            </PlatformAlert>
          )}
        </Stack>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button
            miw={120}
            color="red"
            onClick={() => void handleDeleteDeployment()}
          >
            <FormattedMessage id="deployments.delete" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
